import type {
  CSVExportValidation,
  GenericCSVExportConfig,
  GenericCSVExportParams,
} from '@/domains/shared/csvExport.entity';
import type { GenericCSVExportRepository } from '@/domains/shared/csvExport.repository';

export class GenericCSVExportRepositoryImpl
  implements GenericCSVExportRepository
{
  async exportToCSV<T>(params: GenericCSVExportParams<T>): Promise<void> {
    const { data, config = {} } = params;

    const validation = this.validateData(data);
    if (!validation.isValid) {
      throw new Error(validation.errorMessage);
    }

    const csvContent = this.generateCSVContent(data, config);
    const defaultFilename = `export_${new Date().toISOString().split('T')[0]}.csv`;
    await this.downloadFile(csvContent, config.filename || defaultFilename);
  }

  generateCSVContent<T>(
    data: T[],
    config: GenericCSVExportConfig<T> = {},
  ): string {
    const {
      delimiter = ';',
      includeHeaders = true,
      headersMap = {},
      cellFormatter,
      excludeFields = [],
    } = config;

    if (data.length === 0) {
      return includeHeaders && Object.keys(headersMap).length > 0
        ? Object.values(headersMap).join(delimiter)
        : '';
    }

    const allKeys = Object.keys(data[0]) as Array<keyof T>;
    const keys = allKeys.filter((key) => !excludeFields.includes(key));

    const headers = includeHeaders
      ? keys
          .map((key) => {
            const header = headersMap[key] || String(key);
            return this.escapeCSVValue(header, delimiter);
          })
          .join(delimiter)
      : '';

    const rows = data.map((item) =>
      keys
        .map((key) => {
          if (cellFormatter) {
            return cellFormatter(item[key], key, delimiter);
          }
          return this.defaultFormatCellValue(item[key], key, delimiter);
        })
        .join(delimiter),
    );

    const csvContent = includeHeaders ? [headers, ...rows] : rows;
    return csvContent.join('\n');
  }

  validateData<T>(data: T[]): CSVExportValidation {
    if (!Array.isArray(data)) {
      return {
        isValid: false,
        errorMessage: 'Los datos deben ser un array',
      };
    }

    if (data.length === 0) {
      return {
        isValid: false,
        errorMessage: 'No hay datos para exportar',
      };
    }

    if (typeof data[0] !== 'object' || data[0] === null) {
      return {
        isValid: false,
        errorMessage: 'Los datos deben ser objetos',
      };
    }

    return { isValid: true };
  }

  private defaultFormatCellValue(
    value: any,
    key: any,
    delimiter: string,
  ): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (Array.isArray(value)) {
      value = value.join('; ');
    }

    if (typeof value === 'boolean') {
      value = value ? 'Sí' : 'No';
    }

    if (value instanceof Date) {
      value = value.toISOString().split('T')[0];
    }

    return this.escapeCSVValue(String(value), delimiter);
  }

  private escapeCSVValue(value: string, delimiter: string): string {
    const stringValue = String(value);
    if (
      stringValue.includes(delimiter) ||
      stringValue.includes('"') ||
      stringValue.includes('\n') ||
      stringValue.includes('\r')
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  private async downloadFile(content: string, filename: string): Promise<void> {
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + content], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}
